import { z } from 'zod';

import {
  materialCaptureTypeValues,
  materialCaptureUnitValues,
  materialPresentationTypeValues,
} from '../materials-catalog/index.js';
import {
  cashRoundingApplicationModeValues,
  cashRoundingModeValues,
  purchaseCompletionPrintActionValues,
  purchaseCustomerReceiptCopyPolicyValues,
  purchaseReceiptFolioDocumentTypeValues,
  purchaseReceiptFolioScopeTypeValues,
  purchaseReceiptFolioSequenceStatusValues,
  purchaseReceiptCopyPrintStatusValues,
  purchaseReceiptCopyRoleValues,
  purchaseAdjustmentKindValues,
  purchaseAdjustmentValueTypeValues,
  purchaseIdentifierTypeValues,
  purchaseModeValues,
  purchaseOperatingModeValues,
  purchasePrintAttemptOutcomeValues,
  purchasePrintStatusValues,
  purchaseReceiptModeValues,
  type AdjustmentRecord,
  type HeldPurchase,
  type IdentifierInput,
  type IdentifierSnapshot,
  type PrintStatus,
  type PurchaseCancellation,
  type PurchaseCancellationSummary,
  type PurchaseCashSessionOption,
  type PurchaseCashSessionResolution,
  type PurchaseCashSessionResolutionStatus,
  type PurchaseCompanySettingsSnapshot,
  type PurchaseCommittedSnapshot,
  type PurchaseDraft,
  type PurchaseDraftPayload,
  type PurchaseLineAdjustmentInput,
  type PurchaseHistoryItem,
  type PurchaseCashRoundingSnapshot,
  type ReceiptCopyPrintStatus,
  type PurchaseLimitLedgerEntry,
  type PurchaseLimitLedgerEntryInput,
  type PurchaseLineEntry,
  type PurchaseLinePricing,
  type PurchaseOperatorEmployeeSnapshot,
  type PurchaseOfficialFolioSnapshot,
  type PurchasePostCommitNote,
  type PurchaseReceiptFolioPreview,
  type PurchaseReceiptFolioSequenceSnapshot,
  type PurchaseModeSummary,
  type PurchaseOperatingMode,
  type PurchaseSettingsSnapshot,
  type PurchaseTotals,
  type StationContextRef,
  type PurchaseLineInput,
} from './purchases.dto.js';
import {
  CustomerIdentifierSnapshotSchema,
  CustomerSnapshotSchema,
} from '../customers/customers.schema.js';
import { PurchaseSettlementSummarySchema } from './purchases-settlement.schema.js';

const timestampSchema = z.iso.datetime();
const plateStateSchema = z.string().trim().min(2).max(2);
const operationalWeightScale = 10;
const operationalWeightEpsilon = 1e-6;
const oneDecimalOperationalWeightSchema = z.number().min(0).refine(isOneDecimalOperationalWeight, {
  message: 'Purchase operational weights must be rounded to the nearest 0.1 lb.',
});

export const PurchaseIdentifierTypeSchema = z.enum(purchaseIdentifierTypeValues);
export const PurchaseModeSchema = z.enum(purchaseModeValues);
export const PurchaseOperatingModeSchema: z.ZodType<PurchaseOperatingMode> = z.enum(
  purchaseOperatingModeValues,
);
export const PurchaseCompletionPrintActionSchema = z.enum(purchaseCompletionPrintActionValues);
export const PurchaseReceiptModeSchema = z.enum(purchaseReceiptModeValues);
export const PurchaseReceiptCopyRoleSchema = z.enum(purchaseReceiptCopyRoleValues);
export const PurchaseReceiptCopyPrintStatusSchema = z.enum(purchaseReceiptCopyPrintStatusValues);
export const PurchaseCustomerReceiptCopyPolicySchema = z.enum(
  purchaseCustomerReceiptCopyPolicyValues,
);
export const PurchasePrintAttemptOutcomeSchema = z.enum(purchasePrintAttemptOutcomeValues);
export const PurchaseReceiptFolioDocumentTypeSchema = z.enum(
  purchaseReceiptFolioDocumentTypeValues,
);
export const PurchaseReceiptFolioScopeTypeSchema = z.enum(purchaseReceiptFolioScopeTypeValues);
export const PurchaseReceiptFolioSequenceStatusSchema = z.enum(
  purchaseReceiptFolioSequenceStatusValues,
);
export const CashRoundingModeSchema = z.enum(cashRoundingModeValues);
export const CashRoundingApplicationModeSchema = z.enum(cashRoundingApplicationModeValues);
export const PurchasePrintStatusSchema = z.enum(purchasePrintStatusValues);
const MaterialPresentationTypeSchema = z.enum(materialPresentationTypeValues);
const MaterialCaptureTypeSchema = z.enum(materialCaptureTypeValues);
const MaterialCaptureUnitSchema = z.enum(materialCaptureUnitValues);

export const StationContextRefSchema: z.ZodType<StationContextRef> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
});

export const PurchaseAdjustmentKindSchema = z.enum(purchaseAdjustmentKindValues);
export const PurchaseAdjustmentValueTypeSchema = z.enum(purchaseAdjustmentValueTypeValues);

export const IdentifierInputSchema: z.ZodType<IdentifierInput> = z.object({
  type: PurchaseIdentifierTypeSchema,
  value: z.string().trim().min(1).max(64),
  plateState: plateStateSchema.nullable().optional(),
});

export const IdentifierSnapshotSchema: z.ZodType<IdentifierSnapshot> = z
  .object({
    type: PurchaseIdentifierTypeSchema,
    value: z.string().trim().min(1).max(64),
    plateState: plateStateSchema.nullable(),
    capturedAt: timestampSchema,
  })
  .refine((value) => (value.type === 'plate' ? Boolean(value.plateState) : true), {
    message: 'plateState is required for plate identifiers.',
    path: ['plateState'],
  });

const adjustmentReasonCodeSchema = z.string().trim().min(1).max(100);
const adjustmentReasonNoteSchema = z.string().trim().min(1).max(500);

export const AdjustmentRecordSchema: z.ZodType<AdjustmentRecord> = z
  .object({
    id: z.uuid(),
    presetId: z.uuid().nullable().optional(),
    presetCode: z.string().trim().min(1).max(64).nullable().optional(),
    presetLabel: z.string().trim().min(1).max(120).nullable().optional(),
    kind: PurchaseAdjustmentKindSchema,
    valueType: PurchaseAdjustmentValueTypeSchema,
    value: z.number().nonnegative(),
    reasonCode: adjustmentReasonCodeSchema.nullable().optional(),
    reasonNote: adjustmentReasonNoteSchema.nullable().optional(),
    appliedByUserId: z.uuid().nullable().optional(),
    appliedAt: timestampSchema.nullable().optional(),
  })
  .refine((value) => (value.kind === 'QUALITY_DISCOUNT' ? Boolean(value.reasonCode) : true), {
    message: 'reasonCode is required for quality discounts.',
    path: ['reasonCode'],
  })
  .refine(
    (value) =>
      (value.kind === 'PROMOTION_BONUS' && value.valueType === 'centsPerLb') ||
      (value.kind === 'QUALITY_DISCOUNT' && value.valueType === 'percent'),
    {
      message: 'valueType must match adjustment kind.',
      path: ['valueType'],
    },
  );

export const PurchaseLineAdjustmentInputSchema: z.ZodType<PurchaseLineAdjustmentInput> = z
  .object({
    kind: PurchaseAdjustmentKindSchema,
    valueType: PurchaseAdjustmentValueTypeSchema,
    value: z.number().nonnegative(),
    reasonCode: adjustmentReasonCodeSchema.nullable().optional(),
    reasonNote: adjustmentReasonNoteSchema.nullable().optional(),
  })
  .refine((value) => (value.kind === 'QUALITY_DISCOUNT' ? Boolean(value.reasonCode) : true), {
    message: 'reasonCode is required for quality discounts.',
    path: ['reasonCode'],
  })
  .refine(
    (value) =>
      (value.kind === 'PROMOTION_BONUS' && value.valueType === 'centsPerLb') ||
      (value.kind === 'QUALITY_DISCOUNT' && value.valueType === 'percent'),
    {
      message: 'valueType must match adjustment kind.',
      path: ['valueType'],
    },
  );

export const PurchaseLinePricingSchema: z.ZodType<PurchaseLinePricing> = z.object({
  baseRatePerLb: z.number().nonnegative().nullable(),
  premiumPerLb: z.number().nonnegative().nullable(),
  effectiveRatePerLb: z.number().nonnegative().nullable(),
  depositPerPiece: z.number().nonnegative().nullable(),
  ratePerLb: z.number().nonnegative().nullable(),
  contaminationPerLb: z.number().nonnegative().nullable(),
  qualityDiscountPercent: z.number().nonnegative().nullable().optional(),
  qualityDiscountMoneyCents: z.number().nonnegative().nullable().optional(),
  promoCentsPerLb: z.number().nonnegative().nullable().optional(),
  promoMoneyCents: z.number().nonnegative().nullable().optional(),
});

export const PurchaseLineEntrySchema: z.ZodType<PurchaseLineEntry> = z.object({
  id: z.uuid(),
  materialId: z.uuid(),
  materialDisplayName: z.string().trim().min(1).max(200),
  presentationType: MaterialPresentationTypeSchema,
  captureType: MaterialCaptureTypeSchema,
  unit: MaterialCaptureUnitSchema,
  grossWeightLbs: oneDecimalOperationalWeightSchema.nullable(),
  tareWeightLbs: oneDecimalOperationalWeightSchema.nullable(),
  netWeightLbs: oneDecimalOperationalWeightSchema,
  quantity: z.number().nonnegative().nullable(),
  pricing: PurchaseLinePricingSchema,
  adjustments: z.array(AdjustmentRecordSchema).default([]),
  lineTotal: z.number().min(0),
  equivalentLbs: z.number().min(0).default(0),
  isCrvEligible: z.boolean(),
  isReportedToDoriis: z.boolean(),
});

export const PurchaseLineInputSchema: z.ZodType<PurchaseLineInput> = z.object({
  materialId: z.uuid(),
  presentationType: MaterialPresentationTypeSchema,
  grossWeightLbs: z.number().nonnegative().nullable().optional(),
  tareWeightLbs: z.number().nonnegative().nullable().optional(),
  quantity: z.number().nonnegative().nullable().optional(),
});

export const PurchaseTotalsSchema: z.ZodType<PurchaseTotals> = z.object({
  grossWeightLbs: oneDecimalOperationalWeightSchema,
  tareWeightLbs: oneDecimalOperationalWeightSchema,
  netWeightLbs: oneDecimalOperationalWeightSchema,
  lineSubtotal: z.number().min(0),
  roundingAdjustment: z.number(),
  finalPayout: z.number().min(0),
  crvTotal: z.number().min(0),
  scrapTotal: z.number().min(0),
});

export const PurchaseCashRoundingSnapshotSchema: z.ZodType<PurchaseCashRoundingSnapshot> = z.object(
  {
    roundingApplied: z.boolean(),
    roundingMode: CashRoundingModeSchema,
    roundingIncrement: z.number().positive(),
    roundingAdjustment: z.number(),
    roundingApplicationMode: CashRoundingApplicationModeSchema,
    roundingPolicyVersion: z.number().int().min(0),
  },
);

export const PurchaseOperatorEmployeeSnapshotSchema: z.ZodType<PurchaseOperatorEmployeeSnapshot> =
  z.object({
    employeeId: z.uuid(),
    displayName: z.string().trim().min(1).max(200),
    employeeNumber: z.string().trim().min(1).max(64).nullable(),
    storeId: z.uuid(),
    locationId: z.uuid().nullable(),
    capturedAt: timestampSchema,
    resolutionScope: z.literal('company'),
  });

export const PurchaseCashSessionOptionSchema: z.ZodType<PurchaseCashSessionOption> = z.object({
  cashSessionId: z.uuid(),
  cashLocationId: z.uuid(),
  cashLocationName: z.string().trim().min(1).max(120),
  currentBalanceMinor: z.number().int(),
  openedAt: timestampSchema,
});

export const PurchaseCashSessionResolutionStatusSchema: z.ZodType<PurchaseCashSessionResolutionStatus> =
  z.enum(['selected', 'ambiguous', 'unavailable']);

export const PurchaseCashSessionResolutionSchema: z.ZodType<PurchaseCashSessionResolution> =
  z.object({
    status: PurchaseCashSessionResolutionStatusSchema,
    selectedCashSessionId: z.uuid().nullable(),
    selectedCashLocationId: z.uuid().nullable(),
    options: z.array(PurchaseCashSessionOptionSchema),
  });

export const PurchaseDraftPayloadSchema: z.ZodType<PurchaseDraftPayload> = z.object({
  lineItems: z.array(PurchaseLineEntrySchema),
  identifier: IdentifierInputSchema.nullable(),
  cashRoundingRequested: z.boolean().default(false),
  customerId: z.uuid().nullable().optional(),
  customerSnapshot: CustomerSnapshotSchema.nullable().optional(),
  identifierSnapshot: CustomerIdentifierSnapshotSchema.nullable().optional(),
  notes: z.string().trim().min(1).max(500).nullable().optional(),
});

export const PurchaseDraftSchema: z.ZodType<PurchaseDraft> = z.object({
  id: z.uuid(),
  station: StationContextRefSchema,
  laneId: z.string().trim().min(1).max(64),
  payload: PurchaseDraftPayloadSchema,
  totals: PurchaseTotalsSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const HeldPurchaseSchema: z.ZodType<HeldPurchase> = z.object({
  id: z.uuid(),
  station: StationContextRefSchema,
  laneId: z.string().trim().min(1).max(64),
  payload: PurchaseDraftPayloadSchema,
  totals: PurchaseTotalsSchema,
  lockedByUserId: z.uuid().nullable(),
  lockExpiresAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const ReceiptCopyPrintStatusSchema: z.ZodType<ReceiptCopyPrintStatus> = z.object({
  copyRole: PurchaseReceiptCopyRoleSchema,
  status: PurchaseReceiptCopyPrintStatusSchema,
  printedAt: timestampSchema.nullable(),
  printCount: z.number().int().nonnegative(),
  required: z.boolean(),
});

const ReceiptCopyPrintStatusesSchema: z.ZodType<ReceiptCopyPrintStatus[]> = z
  .array(ReceiptCopyPrintStatusSchema)
  .superRefine(addDuplicateReceiptCopyRoleIssues);

export const PrintStatusSchema: z.ZodType<PrintStatus> = z.object({
  status: PurchasePrintStatusSchema,
  printedAt: timestampSchema.nullable(),
  printCount: z.number().int().nonnegative(),
  receiptCopyStatuses: ReceiptCopyPrintStatusesSchema.optional(),
});

export const PurchaseOfficialFolioSnapshotSchema: z.ZodType<PurchaseOfficialFolioSnapshot> =
  z.object({
    officialFolio: z.string().trim().min(1).max(64),
    folioSequenceId: z.uuid().nullable(),
    folioSequenceScopeType: PurchaseReceiptFolioScopeTypeSchema,
    folioSequenceScopeId: z.uuid(),
    folioDisplayPrefix: z.string().max(24),
    folioSequenceNumber: z.number().int().positive(),
    folioPadding: z.number().int().min(1).max(12),
  });

const PurchaseCommittedLineItemsSchema: z.ZodType<PurchaseLineEntry[]> = z
  .array(PurchaseLineEntrySchema)
  .superRefine(addDuplicatePurchaseLineItemIdIssues);

export const PurchaseCommittedSnapshotSchema: z.ZodType<PurchaseCommittedSnapshot> = z.object({
  id: z.uuid(),
  folioNumber: z.number().int().positive(),
  officialFolio: z.string().trim().min(1).max(64).optional(),
  officialFolioSnapshot: PurchaseOfficialFolioSnapshotSchema.optional(),
  station: StationContextRefSchema,
  laneId: z.string().trim().min(1).max(64),
  operatorUserId: z.uuid(),
  mode: PurchaseModeSchema,
  identifier: IdentifierSnapshotSchema.nullable(),
  customerId: z.uuid().nullable().optional(),
  customerSnapshot: CustomerSnapshotSchema.nullable().optional(),
  identifierSnapshot: CustomerIdentifierSnapshotSchema.nullable().optional(),
  operatorEmployeeSnapshot: PurchaseOperatorEmployeeSnapshotSchema,
  cashSessionId: z.uuid().nullable(),
  cashLocationId: z.uuid().nullable(),
  settlementSummary: PurchaseSettlementSummarySchema.optional(),
  lineItems: PurchaseCommittedLineItemsSchema,
  totals: PurchaseTotalsSchema,
  cashRounding: PurchaseCashRoundingSnapshotSchema,
  occurredAt: timestampSchema,
  createdAt: timestampSchema,
});

export const PurchaseModeSummarySchema: z.ZodType<PurchaseModeSummary> = z.object({
  station: StationContextRefSchema,
  laneId: z.string().trim().min(1).max(64),
  operatorUserId: z.uuid(),
  mode: PurchaseModeSchema,
  identifier: IdentifierSnapshotSchema.nullable(),
  lineItems: z.array(PurchaseLineEntrySchema),
  totals: PurchaseTotalsSchema,
  cashRounding: PurchaseCashRoundingSnapshotSchema,
  occurredAt: timestampSchema,
});

export const PurchaseCompanySettingsSnapshotSchema: z.ZodType<PurchaseCompanySettingsSnapshot> =
  z.object({
    companyId: z.uuid(),
    cashRoundingEnabled: z.boolean(),
    cashRoundingIncrement: z.number().positive(),
    cashRoundingMode: CashRoundingModeSchema,
    cashRoundingApplicationMode: CashRoundingApplicationModeSchema,
    cashRoundingPolicyVersion: z.number().int().min(0),
  });

export const PurchaseSettingsSnapshotSchema: z.ZodType<PurchaseSettingsSnapshot> = z.object({
  station: StationContextRefSchema,
  idRequiredPayoutThreshold: z.number().min(0),
  heldLockTtlMinutes: z.number().int().min(1),
  cancelGraceSeconds: z.number().int().min(0),
  mode: PurchaseOperatingModeSchema,
  cashRoundingEnabled: z.boolean(),
  cashRoundingIncrement: z.number().positive(),
  cashRoundingMode: CashRoundingModeSchema,
  cashRoundingApplicationMode: CashRoundingApplicationModeSchema,
  cashRoundingPolicyVersion: z.number().int().min(0),
  operationalTimeZone: z.string().trim().min(1).optional(),
  operationalTimeZoneSource: z.enum(['location', 'store', 'company', 'system']).optional(),
});

export const PurchaseCancellationSchema: z.ZodType<PurchaseCancellation> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  purchaseId: z.uuid(),
  station: StationContextRefSchema,
  reason: z.string().trim().min(1).max(500),
  replaceWithPurchaseId: z.uuid().nullable(),
  canceledByUserId: z.uuid(),
  approvedByUserId: z.uuid().nullable(),
  canceledAt: timestampSchema,
  createdAt: timestampSchema,
});

export const PurchaseCancellationSummarySchema: z.ZodType<PurchaseCancellationSummary> = z.object({
  reason: z.string().trim().min(1).max(500),
  canceledByUserId: z.uuid(),
  canceledAt: timestampSchema,
});

export const PurchasePostCommitNoteSchema: z.ZodType<PurchasePostCommitNote> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  station: StationContextRefSchema,
  purchaseId: z.uuid().nullable(),
  noteType: z.string().trim().min(1).max(50),
  note: z.string().trim().min(1).max(500),
  createdByUserId: z.uuid(),
  createdAt: timestampSchema,
});

export const PurchaseLimitLedgerEntrySchema = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  operatorUserId: z.uuid(),
  materialId: z.uuid(),
  identifierType: PurchaseIdentifierTypeSchema,
  identifierValue: z.string().trim().min(1).max(64),
  plateState: plateStateSchema.nullable(),
  occurredAt: timestampSchema,
  mode: PurchaseModeSchema,
  totalCrvPaid: z.number().min(0),
  swRatePerLb: z.number().min(0),
  equivalentLbs: z.number().min(0),
  isReversal: z.boolean(),
  reversalOfEntryId: z.uuid().nullable(),
  purchaseId: z.uuid().nullable(),
  createdAt: timestampSchema,
}) satisfies z.ZodType<PurchaseLimitLedgerEntry>;

export const PurchaseLimitLedgerEntryInputSchema: z.ZodType<PurchaseLimitLedgerEntryInput> =
  PurchaseLimitLedgerEntrySchema.omit({
    id: true,
    createdAt: true,
  });

export const PurchaseHistoryItemSchema: z.ZodType<PurchaseHistoryItem> = z.object({
  id: z.uuid(),
  folioNumber: z.number().int().positive(),
  officialFolio: z.string().trim().min(1).max(64).optional(),
  officialFolioSnapshot: PurchaseOfficialFolioSnapshotSchema.optional(),
  station: StationContextRefSchema,
  operatorUserId: z.uuid(),
  totalPayout: z.number().min(0),
  settlementSummary: PurchaseSettlementSummarySchema.optional(),
  occurredAt: timestampSchema,
  printStatus: PrintStatusSchema,
  isCanceled: z.boolean(),
  cancellationSummary: PurchaseCancellationSummarySchema.nullable().optional(),
});

export const PurchaseReceiptFolioPreviewSchema: z.ZodType<PurchaseReceiptFolioPreview> = z.object({
  displayPrefix: z.string().max(24),
  padding: z.number().int().min(1).max(12),
  sequenceNumber: z.number().int().positive(),
  officialFolio: z.string().trim().min(1).max(64),
});

export const PurchaseReceiptFolioSequenceSnapshotSchema: z.ZodType<PurchaseReceiptFolioSequenceSnapshot> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    documentType: PurchaseReceiptFolioDocumentTypeSchema,
    scopeType: PurchaseReceiptFolioScopeTypeSchema,
    scopeId: z.uuid(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid(),
    stationId: z.uuid().nullable(),
    displayPrefix: z.string().max(24),
    padding: z.number().int().min(1).max(12),
    nextNumber: z.number().int().positive(),
    lastIssuedNumber: z.number().int().positive().nullable(),
    lastIssuedAt: timestampSchema.nullable(),
    status: PurchaseReceiptFolioSequenceStatusSchema,
    version: z.number().int().min(1),
    preview: PurchaseReceiptFolioPreviewSchema,
    editable: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    createdByUserId: z.uuid().nullable(),
    updatedByUserId: z.uuid().nullable(),
  });

function isOneDecimalOperationalWeight(value: number): boolean {
  if (!Number.isFinite(value)) {
    return false;
  }
  const scaled = value * operationalWeightScale;
  return Math.abs(scaled - Math.round(scaled)) <= operationalWeightEpsilon;
}

function addDuplicatePurchaseLineItemIdIssues(
  lineItems: readonly PurchaseLineEntry[],
  context: z.RefinementCtx,
): void {
  const seenIds = new Set<string>();
  for (const [index, lineItem] of lineItems.entries()) {
    if (seenIds.has(lineItem.id)) {
      context.addIssue({
        code: 'custom',
        message: 'Purchase line item ids must be unique.',
        path: [index, 'id'],
      });
      continue;
    }
    seenIds.add(lineItem.id);
  }
}

function addDuplicateReceiptCopyRoleIssues(
  statuses: readonly ReceiptCopyPrintStatus[],
  context: z.RefinementCtx,
): void {
  const seenRoles = new Set<ReceiptCopyPrintStatus['copyRole']>();
  for (const [index, status] of statuses.entries()) {
    if (seenRoles.has(status.copyRole)) {
      context.addIssue({
        code: 'custom',
        message: 'Receipt copy roles must be unique.',
        path: [index, 'copyRole'],
      });
      continue;
    }
    seenRoles.add(status.copyRole);
  }
}
